import { useAuth } from '@clerk/clerk-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Pill } from '@/components/ui/Pill'
import { countryName } from '@/data/countries'
import { listAdminChecks, resolveAdminCheck } from '@/lib/api/admin'
import { ApiError } from '@/lib/api/client'
import { cn } from '@/lib/cn'
import type { AdminListing, ResolveCheckAction } from '@/lib/types/admin'

const REASON_LABEL: Record<string, string> = {
  adult: 'Adult site',
  download: 'File download',
  http_error: 'HTTP error',
  ip_host: 'IP address host',
  webrisk: 'Malware or phishing',
  webrisk_error: 'Web Risk unavailable',
  timeout: 'Timed out',
  unreachable: 'Unreachable',
  redirect: 'Bad redirect',
}

function reasonLabel(reason: string | null): string {
  if (!reason) return 'Failed check'
  return REASON_LABEL[reason] ?? reason
}

function formatWhen(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function actionError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return "You don't have access."
    if (err.status === 409) return 'Already resolved.'
    return err.detail
  }
  return "Couldn't update that check."
}

export function AdminChecks() {
  const { getToken } = useAuth()
  const [items, setItems] = useState<AdminListing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [mobileDetail, setMobileDetail] = useState(false)

  const selected = items.find((item) => item.id === selectedId) ?? null

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const next = await listAdminChecks(getToken)
      setItems(next)
      setSelectedId((current) => {
        if (current && next.some((item) => item.id === current)) {
          return current
        }
        return next[0]?.id ?? null
      })
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Couldn't load checks.")
      setItems([])
      setSelectedId(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [getToken])

  async function resolve(listing: AdminListing, action: ResolveCheckAction) {
    const ok =
      action === 'approve'
        ? window.confirm(`Put ${listing.brand} live?`)
        : window.confirm(`Take ${listing.brand} down?`)
    if (!ok) {
      return
    }

    setBusy(true)
    setError(null)
    try {
      await resolveAdminCheck(listing.id, action, getToken)
      const next = await listAdminChecks(getToken)
      setItems(next)
      setSelectedId(next[0]?.id ?? null)
      setMobileDetail(false)
    } catch (err) {
      setError(actionError(err))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <p className="font-body text-body-md text-fg-muted">Loading…</p>
  }

  if (items.length === 0 && !error) {
    return (
      <p className="font-body text-body-md text-fg-muted">
        No failed checks waiting.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      {error && (
        <p className="font-body text-body-sm text-error" role="alert">
          {error}
        </p>
      )}
      <div className="border-glass-border grid min-h-[28rem] overflow-hidden rounded-lg border md:grid-cols-[minmax(16rem,20rem)_1fr]">
        <ul
          className={cn(
            'border-glass-border divide-y overflow-y-auto md:border-r',
            mobileDetail && 'hidden md:block',
          )}
        >
          {items.map((item) => {
            const active = item.id === selectedId
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(item.id)
                    setMobileDetail(true)
                  }}
                  className={cn(
                    'w-full px-4 py-3 text-left transition-colors',
                    active
                      ? 'bg-primary-container/15'
                      : 'hover:bg-white/5',
                  )}
                >
                  <p className="font-display text-fg truncate font-semibold">
                    {item.brand}
                  </p>
                  <p className="font-body text-body-sm text-fg-muted truncate">
                    {reasonLabel(item.linkCheckReason)}
                  </p>
                  <p className="font-body text-label-sm text-fg-muted mt-1">
                    {formatWhen(item.createdAt)}
                  </p>
                </button>
              </li>
            )
          })}
        </ul>

        <section
          className={cn(
            'flex flex-col gap-5 p-5',
            !mobileDetail && 'hidden md:flex',
          )}
        >
          {selected ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setMobileDetail(false)
                }}
                className="font-body text-label-sm text-primary hover:text-primary-fixed-dim self-start md:hidden"
              >
                Back to list
              </button>

              <div className="space-y-2">
                <h2 className="font-display text-headline-md text-fg">
                  {selected.brand}
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="neutral">{selected.type}</Pill>
                  <Pill tone="error">
                    {reasonLabel(selected.linkCheckReason)}
                  </Pill>
                </div>
                <p className="font-body text-label-sm text-fg-muted">
                  {formatWhen(selected.createdAt)}
                </p>
              </div>

              {selected.benefitClaim && (
                <p className="font-body text-body-md text-fg text-pretty">
                  {selected.benefitClaim}
                </p>
              )}

              <p className="font-body text-body-sm text-fg-muted break-all">
                <span className="text-label-caps uppercase">URL · </span>
                <a
                  href={selected.destinationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-fixed-dim underline underline-offset-2"
                >
                  {selected.destinationUrl}
                </a>
              </p>

              <p className="font-body text-label-sm text-fg-muted">
                Posted by @{selected.posterUsername}
                {selected.category ? ` · ${selected.category}` : ''}
                {selected.country
                  ? ` · ${countryName(selected.country) ?? selected.country}`
                  : ''}
              </p>

              <div className="mt-auto flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  disabled={busy}
                  onClick={() => {
                    void resolve(selected, 'approve')
                  }}
                >
                  {busy ? 'Working…' : 'Approve'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  disabled={busy}
                  onClick={() => {
                    void resolve(selected, 'reject')
                  }}
                >
                  Reject
                </Button>
              </div>
            </>
          ) : (
            <p className="font-body text-body-md text-fg-muted">
              Select a check.
            </p>
          )}
        </section>
      </div>
    </div>
  )
}
